// src/pages/ForgotPasswordScholar.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Unified grade options 1–12
const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

function getApiBase() {
  const raw = (process.env.REACT_APP_API_BASE_URL || '').trim();
  return raw.replace(/\/+$/, '');
}

async function parseResponse(res) {
  const text = await res.text().catch(() => '');
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {}
  return { ok: res.ok, status: res.status, data, text };
}

export default function ForgotPasswordScholar() {
  const [email, setEmail] = useState('');
  const [lunch, setLunch] = useState('');
  const [grade, setGrade] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const apiBase = getApiBase();

  function validate() {
    if (!apiBase) return 'Missing API base URL. Set REACT_APP_API_BASE_URL.';
    if (!email || !lunch || !grade || !pw1 || !pw2) return 'Please fill out all fields.';
    if (pw1 !== pw2) return 'Passwords do not match.';
    if (pw1.length < 6) return 'Password must be at least 6 characters.';
    return '';
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (busy) return;

    setErr('');
    setMsg('');

    const v = validate();
    if (v) {
      setErr(v);
      return;
    }

    setBusy(true);
    try {
      // ✅ Correct backend route (matches backend/apps/accounts/urls.py)
      // POST /api/auth/password-reset/scholar-self/
      const url = `${apiBase}/api/auth/password-reset/scholar-self/`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({
          role: 'scholar',
          email: (email || '').trim().toLowerCase(),
          lunch_number: String(lunch || '').trim(),
          grade: String(grade || '').trim(),
          new_password: pw1,
        }),
      });

      const { ok, status, data, text } = await parseResponse(res);

      if (!ok) {
        // DRF error formats can be:
        // - { detail: "..." }
        // - { field: ["..."] }
        // - plain text
        let detail =
          (data && (data.detail || data.error || data.message)) ||
          '';

        if (!detail && data && typeof data === 'object') {
          // Flatten field errors like { lunch_number: ["..."], grade: ["..."] }
          const parts = [];
          for (const [k, v] of Object.entries(data)) {
            if (Array.isArray(v)) parts.push(`${k}: ${v.join(' ')}`);
            else if (typeof v === 'string') parts.push(`${k}: ${v}`);
          }
          detail = parts.join(' | ');
        }

        detail = detail || text || `HTTP ${status}`;
        setErr(detail || 'Password reset failed.');
        return;
      }

      setMsg(
        (data && (data.detail || data.message)) ||
          'Password updated! You can sign in with your new password.'
      );

      setEmail('');
      setLunch('');
      setGrade('');
      setPw1('');
      setPw2('');
    } catch (e) {
      setErr('Network error.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <img
          src="/icons/Propel_schools_logo.png"
          alt="Propel Schools"
          width={48}
          height={48}
          style={{ justifySelf: 'center' }}
        />
        <h1 style={styles.h1}>Scholar Password Reset</h1>
        <p style={styles.p}>
          Enter your school email, lunch number, and grade. After verification, you can set a new password.
        </p>

        <form onSubmit={onSubmit} style={{ display: 'grid', gap: 10 }}>
          <label style={styles.label} htmlFor="email">School Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="first.last@propelschools.org"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            disabled={busy}
            required
          />

          <label style={styles.label} htmlFor="lunch">Lunch Number</label>
          <input
            id="lunch"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Your lunch number"
            value={lunch}
            onChange={(e) => setLunch(e.target.value)}
            style={styles.input}
            disabled={busy}
            required
          />

          <label style={styles.label} htmlFor="grade">Grade</label>
          <select
            id="grade"
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            style={{ ...styles.input, height: 48 }}
            disabled={busy}
            required
          >
            <option value="" disabled>Choose your grade</option>
            {GRADE_OPTIONS.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>

          <label style={styles.label} htmlFor="pw1">New Password</label>
          <input
            id="pw1"
            type="password"
            autoComplete="new-password"
            placeholder="Create a new password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            style={styles.input}
            disabled={busy}
            required
          />

          <label style={styles.label} htmlFor="pw2">Confirm Password</label>
          <input
            id="pw2"
            type="password"
            autoComplete="new-password"
            placeholder="Repeat your new password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            style={styles.input}
            disabled={busy}
            required
          />

          <button type="submit" style={styles.btn} disabled={busy}>
            {busy ? 'Submitting…' : 'Reset Password'}
          </button>

          {msg && <div role="status" style={{ color: '#0a7d0a', fontWeight: 700 }}>{msg}</div>}
          {err && <div role="alert" style={{ color: '#b00020', fontWeight: 700 }}>{err}</div>}
        </form>

        <div style={styles.footer}>
          <Link to="/recover" style={styles.link}>Back: choose Educator or Scholar</Link>
          <span style={{ margin: '0 6px' }}>·</span>
          <Link to="/welcome" style={styles.link}>Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: { minHeight: '100vh', display: 'grid', placeItems: 'center', 
    background: '#F7F9FC', padding: 24 },
  card: { width: 460, maxWidth: '92vw', background: '#fff', padding: 24, 
    borderRadius: 16, boxShadow: '0 10px 24px rgba(0,0,0,0.08)', display: 'grid', gap: 12 },
  h1: { margin: 0, fontSize: 22, color: '#003e73', textAlign: 'center' },
  p: { margin: '0 0 8px 0', color: '#042d60', textAlign: 'center' },
  label: { fontWeight: 700, color: '#042d60' },
  input: { width: '100%', height: 44, border: '1px solid rgba(0,0,0,0.12)', 
    borderRadius: 12, padding: '0 12px' },
  btn: { height: 48, border: 'none', borderRadius: 999, background: '#f9a21a', 
    color: '#fff', fontWeight: 900, cursor: 'pointer' },
  footer: { marginTop: 8, textAlign: 'center' },
  link: { color: '#003e73', textDecoration: 'underline', fontWeight: 700 }
};
